import { useMemo } from 'react';

import type { MatchFeedPreviewCopy } from './matchFeedPreviewCopy';
import { splitPreviewSkills } from './previewJobHelpers';
import type { HeroPreviewCopy } from './heroPreviewCopy';
import { useHeroPreviewCopy } from './useHeroPreviewCopy';
import { useMatchFeedPreviewCopy } from './useMatchFeedPreviewCopy';

export type PreviewJobFixture = {
  id: string;
  title: string;
  company: string;
  location: string;
  score: number;
  source: string;
  skills: string[];
  jobType: string;
  experienceLevel: string;
  salary: string;
  postedLabel: string;
  remote?: boolean;
  saved?: boolean;
  description?: string;
};

export type FeedJobFixture = PreviewJobFixture & {
  band: 'strong_fit' | 'good_fit' | 'partial_fit';
  why: string;
  matchedSkills: string[];
  missingSkills: string[];
  bonusSkills: string[];
  actionItems: string[];
  dims: {
    skill_overlap: number;
    semantic_similarity: number;
    experience_alignment: number;
    budget_fit: number;
    location_fit: number;
    deadline_feasibility: number;
  };
};

function buildHeroMockJobs(p: HeroPreviewCopy): PreviewJobFixture[] {
  return [
    {
      id: 'hero-1',
      title: p.matchOneTitle,
      company: p.matchOneCompany,
      location: 'Bengaluru',
      score: 94,
      skills: splitPreviewSkills(p.mockSkillsMatch1),
      saved: true,
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expMidSenior,
      postedLabel: p.timePosted2d,
      source: 'linkedin',
      description: p.mockDescMatch1,
    },
    {
      id: 'hero-2',
      title: p.matchTwoTitle,
      company: p.matchTwoCompany,
      location: p.locationRemote,
      score: 88,
      skills: splitPreviewSkills(p.mockSkillsMatch2),
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expSenior,
      postedLabel: p.timePosted1d,
      source: 'linkedin',
      description: p.mockDescMatch2,
    },
    {
      id: 'hero-3',
      title: p.mockJobStaffPm,
      company: p.mockCompanyGrowthSaaS,
      location: 'Bangalore',
      score: 85,
      skills: splitPreviewSkills(p.mockSkillsStaff),
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expStaff,
      postedLabel: p.timePosted3d,
      source: 'peerlist',
      description: p.mockDescStaff,
    },
    {
      id: 'hero-4',
      title: p.mockJobPrincipalPm,
      company: p.mockCompanyEnterprise,
      location: p.locationRemote,
      score: 82,
      skills: splitPreviewSkills(p.mockSkillsPrincipal),
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expPrincipal,
      postedLabel: p.timePosted5d,
      source: 'naukri',
      description: p.mockDescPrincipal,
    },
    {
      id: 'hero-5',
      title: p.mockJobAssociatePm,
      company: p.mockCompanyFlipkart,
      location: 'Hyderabad',
      score: 79,
      skills: splitPreviewSkills(p.mockSkillsAssociate),
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expAssociate,
      postedLabel: p.timePosted4d,
      source: 'naukri',
      description: p.mockDescAssociate,
    },
    {
      id: 'hero-6',
      title: p.mockJobGroupPm,
      company: p.mockCompanyAmazon,
      location: p.locationRemote,
      score: 76,
      skills: splitPreviewSkills(p.mockSkillsGroup),
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expGroup,
      postedLabel: p.timePosted6d,
      source: 'linkedin',
      description: p.mockDescGroup,
    },
    {
      id: 'hero-7',
      title: p.mockJobLeadPm,
      company: p.mockCompanyStripe,
      location: 'San Francisco',
      score: 74,
      skills: splitPreviewSkills(p.mockSkillsLead),
      remote: true,
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expLead,
      postedLabel: p.timePosted1w,
      source: 'linkedin',
      description: p.mockDescLead,
    },
    {
      id: 'hero-8',
      title: p.mockJobPmGeneric,
      company: p.mockCompanyQube,
      location: 'Chennai',
      score: 84,
      skills: splitPreviewSkills(p.mockSkillsGeneric),
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expMidSenior,
      postedLabel: p.timePosted3d,
      source: 'naukri',
      description: p.mockDescGeneric,
    },
  ];
}

function buildAtsPreviewJobs(p: HeroPreviewCopy, copy: MatchFeedPreviewCopy): PreviewJobFixture[] {
  return [
    {
      id: 'job-bad',
      title: p.mockJobStaffPm,
      company: p.mockCompanyEnterprise,
      location: p.locationRemote,
      score: 58,
      source: 'LinkedIn',
      skills: splitPreviewSkills(p.mockSkillsPrincipal),
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expStaffYears,
      salary: p.salaryNotDisclosed,
      postedLabel: p.timePosted5d,
    },
    {
      id: 'job-good',
      title: copy.feedJob1Title,
      company: copy.feedJob1Company,
      location: copy.feedJob1Location,
      score: 91,
      source: 'LinkedIn',
      skills: splitPreviewSkills(copy.feedJob1Skills),
      jobType: p.jobTypeFullTime,
      experienceLevel: copy.feedJob1Exp,
      salary: copy.feedJob1Salary,
      postedLabel: p.timePosted1d,
    },
    {
      id: 'job-3',
      title: p.atsJobPmTitle,
      company: p.atsJobPmCompany,
      location: 'Bengaluru',
      score: 84,
      source: 'Naukri',
      skills: splitPreviewSkills(p.atsJobPmSkills),
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expMidSeniorYears,
      salary: p.atsJobPmSalary,
      postedLabel: p.timePosted2d,
    },
    {
      id: 'job-4',
      title: p.atsJobLeadPlatformTitle,
      company: p.atsJobLeadPlatformCompany,
      location: 'Bangalore',
      score: 79,
      source: 'Peerlist',
      skills: splitPreviewSkills(p.atsJobLeadPlatformSkills),
      jobType: p.jobTypeFullTime,
      experienceLevel: p.expLeadYears,
      salary: p.atsJobLeadPlatformSalary,
      postedLabel: p.timePosted3d,
    },
  ];
}

function buildFeedJobs(p: HeroPreviewCopy, copy: MatchFeedPreviewCopy): FeedJobFixture[] {
  return [
    {
      id: 'fj1',
      title: copy.feedJob1Title,
      company: copy.feedJob1Company,
      location: copy.feedJob1Location,
      score: 96,
      source: 'LinkedIn',
      band: 'strong_fit',
      skills: splitPreviewSkills(copy.feedJob1Skills),
      jobType: p.jobTypeFullTime,
      experienceLevel: copy.feedJob1Exp,
      salary: copy.feedJob1Salary,
      postedLabel: p.timePosted1d,
      why: copy.sampleWhy,
      matchedSkills: splitPreviewSkills(copy.feedJob1MatchedSkills),
      missingSkills: splitPreviewSkills(copy.feedJob1MissingSkills),
      bonusSkills: splitPreviewSkills(copy.feedJob1BonusSkills),
      actionItems: splitPreviewSkills(copy.feedJob1ActionItems),
      dims: {
        skill_overlap: 96,
        semantic_similarity: 94,
        experience_alignment: 94,
        budget_fit: 90,
        location_fit: 100,
        deadline_feasibility: 92,
      },
    },
    {
      id: 'fj2',
      title: copy.feedJob2Title,
      company: copy.feedJob2Company,
      location: copy.feedJob2Location,
      score: 91,
      source: 'Naukri',
      band: 'strong_fit',
      skills: splitPreviewSkills(copy.feedJob2Skills),
      jobType: p.jobTypeFullTime,
      experienceLevel: copy.feedJob2Exp,
      salary: copy.feedJob2Salary,
      postedLabel: p.timePosted2d,
      why: copy.feedJob2Why,
      matchedSkills: splitPreviewSkills(copy.feedJob2MatchedSkills),
      missingSkills: splitPreviewSkills(copy.feedJob2MissingSkills),
      bonusSkills: splitPreviewSkills(copy.feedJob2BonusSkills),
      actionItems: splitPreviewSkills(copy.feedJob2ActionItems),
      dims: {
        skill_overlap: 90,
        semantic_similarity: 88,
        experience_alignment: 88,
        budget_fit: 86,
        location_fit: 82,
        deadline_feasibility: 98,
      },
    },
    {
      id: 'fj3',
      title: copy.feedJob3Title,
      company: copy.feedJob3Company,
      location: copy.feedJob3Location,
      score: 87,
      source: 'Peerlist',
      band: 'good_fit',
      skills: splitPreviewSkills(copy.feedJob3Skills),
      jobType: p.jobTypeFullTime,
      experienceLevel: copy.feedJob3Exp,
      salary: copy.feedJob3Salary,
      postedLabel: p.timePosted3d,
      why: copy.feedJob3Why,
      matchedSkills: splitPreviewSkills(copy.feedJob3MatchedSkills),
      missingSkills: splitPreviewSkills(copy.feedJob3MissingSkills),
      bonusSkills: splitPreviewSkills(copy.feedJob3BonusSkills),
      actionItems: splitPreviewSkills(copy.feedJob3ActionItems),
      dims: {
        skill_overlap: 84,
        semantic_similarity: 82,
        experience_alignment: 90,
        budget_fit: 80,
        location_fit: 96,
        deadline_feasibility: 88,
      },
    },
    {
      id: 'fj4',
      title: copy.feedJob4Title,
      company: copy.feedJob4Company,
      location: copy.feedJob4Location,
      score: 72,
      source: 'LinkedIn',
      band: 'partial_fit',
      skills: splitPreviewSkills(copy.feedJob4Skills),
      jobType: p.jobTypeFullTime,
      experienceLevel: copy.feedJob4Exp,
      salary: p.salaryNotDisclosed,
      postedLabel: p.timePosted5d,
      why: copy.feedJob4Why,
      matchedSkills: splitPreviewSkills(copy.feedJob4MatchedSkills),
      missingSkills: splitPreviewSkills(copy.feedJob4MissingSkills),
      bonusSkills: splitPreviewSkills(copy.feedJob4BonusSkills),
      actionItems: splitPreviewSkills(copy.feedJob4ActionItems),
      dims: {
        skill_overlap: 80,
        semantic_similarity: 76,
        experience_alignment: 86,
        budget_fit: 88,
        location_fit: 72,
        deadline_feasibility: 90,
      },
    },
  ];
}

export function useHeroMockJobs(): PreviewJobFixture[] {
  const p = useHeroPreviewCopy();
  return useMemo(() => buildHeroMockJobs(p), [p]);
}

export function useAtsPreviewJobs(): PreviewJobFixture[] {
  const p = useHeroPreviewCopy();
  const copy = useMatchFeedPreviewCopy();
  return useMemo(() => buildAtsPreviewJobs(p, copy), [p, copy]);
}

export function useFeedPreviewJobs(): FeedJobFixture[] {
  const p = useHeroPreviewCopy();
  const copy = useMatchFeedPreviewCopy();
  return useMemo(() => buildFeedJobs(p, copy), [p, copy]);
}

/** Agentic job search feature preview — reuses feed job 1 + hero/ats fixtures. */
export function useAgenticPreviewJobs(): Array<
  PreviewJobFixture & {
    description?: string;
    matchedSkills?: string[];
    missingSkills?: string[];
    bonusSkills?: string[];
    whyMatched?: string;
    actionItems?: string[];
  }
> {
  const p = useHeroPreviewCopy();
  const copy = useMatchFeedPreviewCopy();
  return useMemo(
    () => [
      {
        id: 'job-good',
        title: copy.feedJob1Title,
        company: copy.feedJob1Company,
        location: copy.feedJob1Location,
        score: 91,
        skills: splitPreviewSkills(copy.feedJob1Skills),
        source: 'LinkedIn',
        jobType: p.jobTypeFullTime,
        experienceLevel: copy.feedJob1Exp,
        salary: copy.feedJob1Salary,
        postedLabel: p.timePosted1d,
        description: copy.feedJob1Description,
        matchedSkills: splitPreviewSkills(copy.feedJob1MatchedSkills),
        missingSkills: splitPreviewSkills(copy.feedJob1MissingSkills),
        bonusSkills: splitPreviewSkills(copy.feedJob1BonusSkills),
        whyMatched: copy.sampleWhy,
        actionItems: splitPreviewSkills(copy.feedJob1ActionItems),
      },
      {
        id: 'job-2',
        title: p.atsJobPmTitle,
        company: p.atsJobPmCompany,
        location: 'Bengaluru',
        score: 84,
        skills: splitPreviewSkills(p.atsJobPmSkills),
        source: 'Naukri',
        jobType: p.jobTypeFullTime,
        experienceLevel: p.expMidSeniorYears,
        salary: p.atsJobPmSalary,
        postedLabel: p.timePosted2d,
        description: p.atsJobPmDescription,
        matchedSkills: splitPreviewSkills(p.atsJobPmMatchedSkills),
        missingSkills: splitPreviewSkills(p.atsJobPmMissingSkills),
        bonusSkills: splitPreviewSkills(p.atsJobPmBonusSkills),
        whyMatched: p.atsJobPmWhy,
        actionItems: splitPreviewSkills(p.atsJobPmActionItems),
      },
      {
        id: 'job-3',
        title: p.atsJobLeadPlatformTitle,
        company: p.mockCompanyGrowthSaaS,
        location: `${copy.feedJob3Location} · ${p.locationRemote}`,
        score: 87,
        skills: splitPreviewSkills(p.atsJobLeadPlatformSkills),
        source: 'Peerlist',
        jobType: p.jobTypeFullTime,
        experienceLevel: p.expLeadYears,
        salary: p.atsJobLeadPlatformSalary,
        postedLabel: p.timePosted3d,
        description: p.atsJobLeadPlatformDescription,
        matchedSkills: splitPreviewSkills(p.atsJobLeadPlatformMatchedSkills),
        missingSkills: splitPreviewSkills(p.atsJobLeadPlatformMissingSkills),
        bonusSkills: splitPreviewSkills(p.atsJobLeadPlatformBonusSkills),
        whyMatched: p.atsJobLeadPlatformWhy,
        actionItems: splitPreviewSkills(p.atsJobLeadPlatformActionItems),
      },
      {
        id: 'job-4',
        title: p.mockJobStaffPm,
        company: p.mockCompanyEnterprise,
        location: p.locationRemote,
        score: 84,
        skills: splitPreviewSkills(p.mockSkillsPrincipal),
        source: 'LinkedIn',
        jobType: p.jobTypeFullTime,
        experienceLevel: p.expStaffYears,
        salary: p.salaryNotDisclosed,
        postedLabel: p.timePosted5d,
        description: p.mockDescPrincipal,
        matchedSkills: splitPreviewSkills(p.atsJobStaffMatchedSkills),
        missingSkills: splitPreviewSkills(p.atsJobStaffMissingSkills),
        bonusSkills: splitPreviewSkills(p.atsJobStaffBonusSkills),
        whyMatched: p.atsJobStaffWhy,
        actionItems: splitPreviewSkills(p.atsJobStaffActionItems),
      },
    ],
    [p, copy],
  );
}
